<script setup lang="ts">
import { useLayout } from '@/layout/composables/layout'
import { computed, onMounted, ref, watch } from 'vue'
import AppFooter from './AppFooter.vue'
import AppSidebar from './AppSidebar.vue'
import AppTopbar from './AppTopbar.vue'
import { useUserClientConfigStore } from '@/stores/userClientConfigStore.ts'
import { useRoute } from 'vue-router'
import { isObjectEmpty } from '@/assets/js/main.ts'

const userClientConfigStore = useUserClientConfigStore()

const {
  layoutConfig,
  layoutState,
  isSidebarActive,
  executeDarkModeToggle,
  isDarkTheme
} = useLayout()

const outsideClickListener = ref(null)

const isStaging = import.meta.env.MODE === 'staging'

onMounted(() => {
  if (userClientConfigStore.isDarkMode() && !isDarkTheme.value) {
    executeDarkModeToggle()
  }
})

watch(isSidebarActive, (newVal) => {
  if (newVal) {
    bindOutsideClickListener()
  } else {
    unbindOutsideClickListener()
  }
})

const containerClass = computed(() => {
  return {
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-static-inactive':
      layoutState.staticMenuDesktopInactive &&
      layoutConfig.menuMode === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.staticMenuMobileActive,
    'layout-staging': isStaging
  }
})

function bindOutsideClickListener() {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        layoutState.overlayMenuActive = false
        layoutState.staticMenuMobileActive = false
        layoutState.menuHoverActive = false
      }
    }
    document.addEventListener('click', outsideClickListener.value)
  }
}

function unbindOutsideClickListener() {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener)
    outsideClickListener.value = null
  }
}

function isOutsideClicked(event) {
  const sidebarEl = document.querySelector('.layout-sidebar')
  const topbarEl = document.querySelector('.layout-menu-button')

  return !(
    sidebarEl.isSameNode(event.target) ||
    sidebarEl.contains(event.target) ||
    topbarEl.isSameNode(event.target) ||
    topbarEl.contains(event.target)
  )
}

const home = ref({
  icon: 'pi pi-home',
  route: '/'
})

const route = useRoute()

const breadcrumbItems = computed(() => {
  const matched = route.matched.filter(
    (r) => !isObjectEmpty(r.meta) && r.meta?.breadcrumb != null
  )
  return matched.map((r, index) => {
    return {
      label:
        typeof r.meta?.breadcrumb === 'function'
          ? r.meta.breadcrumb(route)
          : r.meta?.breadcrumb || r.name,
      to:
        index < matched.length - 1
          ? { name: r.name, params: route.params }
          : null
    }
  })
})

const showBreadcrumb = computed(() => {
  return route.name !== 'home' && route.name !== 'profile'
})
</script>

<template>
  <div class="layout-wrapper" :class="containerClass">
    <div v-if="isStaging" class="staging-banner">AMBIENTE DI STAGING</div>
    <app-topbar />
    <app-sidebar />
    <div class="layout-main-container">
      <div class="layout-main">
        <!-- BREADCRUMB -->
        <Breadcrumb
          v-if="showBreadcrumb"
          :home="home"
          :model="breadcrumbItems"
          class="mb-4"
        >
          <template #item="{ item, props }">
            <router-link
              v-if="item.route"
              v-slot="{ href, navigate }"
              :to="item.route"
              custom
            >
              <a :href="href" v-bind="props.action" @click="navigate">
                <span :class="[item.icon, 'text-color']" />
                <span class="text-primary font-semibold">
                  {{ item.label }}
                </span>
              </a>
            </router-link>
            <a
              v-else
              :href="item.url"
              :target="item.target"
              v-bind="props.action"
            >
              <span class="text-surface-700 dark:text-surface-0">
                {{ item.label }}
              </span>
            </a>
          </template>
        </Breadcrumb>
        <!-- ROUTER VIEW -->
        <router-view />
      </div>
      <app-footer />
    </div>
    <div class="layout-mask animate-fadein" />
  </div>
  <Toast />
</template>

<style lang="scss" scoped>
/* override of Breadcrumb style */
.p-breadcrumb {
  // background-color: #f5f5f5 !important;
}
</style>
